import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Space,
  Tag,
  Tooltip,
  Typography,
  Modal,
  Empty,
  Badge,
  Descriptions,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  UserOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchSegments,
  deleteSegment,
  selectSegments,
  selectPushLoading,
} from 'store/slices/push.slice';
import { usePermissions } from 'hooks/usePermissions';
import { Segment, SegmentCondition } from 'types/push.types';
import { formatters } from 'utils/formatters';
import { notify } from 'utils/helpers';
import { ROUTES } from 'config/routes';

const { confirm } = Modal;
const { Text } = Typography;

interface Props {
  appId: number | null;
}

const getUserCountColor = (count: number) => {
  if (count === 0) return '#d9d9d9';
  if (count < 100) return '#faad14';
  if (count < 1000) return '#52c41a';
  return '#1890ff';
};

const fieldLabels: Record<string, string> = {
  has_registration: 'Регистрация',
  has_deposit: 'Депозит',
  locale: 'Локаль',
  country: 'Страна',
  os: 'ОС',
  offer: 'Оффер',
  buyer_id: 'Байер',
  install_date: 'Дата установки',
};

const operatorLabels: Record<string, string> = {
  equals: '=',
  greater_than: '>',
  less_than: '<',
  contains: 'содержит',
  not_contains: 'не содержит',
};

const getConditionDisplay = (condition: SegmentCondition): string => {
  const field = fieldLabels[condition.field] || condition.field;
  const operator = operatorLabels[condition.operator] || condition.operator;
  let value = condition.value;
  if (typeof value === 'boolean') value = value ? 'Да' : 'Нет';
  return `${field} ${operator} ${value}`;
};

const ExpandedRow: React.FC<{ record: Segment }> = ({ record }) => {
  const conditions = record.conditions || [];
  const count = record.user_count;

  return (
    <div style={{ padding: '8px 48px 16px', background: 'var(--color-bg-elevated)', borderRadius: 6 }}>
      <Space size={32} align="start" wrap>
        <div>
          <Space style={{ marginBottom: 8 }}>
            <FilterOutlined style={{ color: '#1890ff' }} />
            <Text strong>Условия сегмента</Text>
            <Text type="secondary">({conditions.length})</Text>
          </Space>
          {conditions.length === 0 ? (
            <div>
              <Tag>Без условий — все пользователи</Tag>
            </div>
          ) : (
            <Space size={6} wrap>
              {conditions.map((cond, idx) => (
                <Tag key={idx} color="blue" style={{ marginBottom: 4 }}>
                  {getConditionDisplay(cond)}
                </Tag>
              ))}
            </Space>
          )}
        </div>

        <Divider type="vertical" style={{ height: 'auto', alignSelf: 'stretch' }} />
        <div>
          <Space style={{ marginBottom: 8 }}>
            <UserOutlined style={{ color: '#52c41a' }} />
            <Text strong>Аудитория</Text>
          </Space>
          <Descriptions column={1} size="small" style={{ minWidth: 180 }}>
            <Descriptions.Item label="Пользователей в сегменте">
              <Badge
                count={formatters.number(count)}
                style={{ backgroundColor: getUserCountColor(count) }}
                overflowCount={9999999}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Создан">
              {formatters.date(record.created_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Обновлён">
              {formatters.timeAgo(record.updated_at)}
            </Descriptions.Item>
          </Descriptions>
        </div>
        {record.description && (
          <>
            <Divider type="vertical" style={{ height: 'auto', alignSelf: 'stretch' }} />
            <div style={{ maxWidth: 300 }}>
              <Text strong style={{ display: 'block', marginBottom: 6 }}>Описание</Text>
              <Text type="secondary">{record.description}</Text>
            </div>
          </>
        )}
      </Space>
    </div>
  );
};

const SegmentList: React.FC<Props> = ({ appId }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const permissions = usePermissions();

  const segments = useAppSelector(selectSegments);
  const loading = useAppSelector(selectPushLoading);

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const handleDelete = (segment: Segment) => {
    confirm({
      title: 'Удалить сегмент?',
      content: `Вы уверены, что хотите удалить сегмент "${segment.name}"? Это действие нельзя отменить.`,
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await dispatch(deleteSegment(segment.id)).unwrap();
          notify.success('Сегмент успешно удален');
          if (appId) {
            dispatch(fetchSegments({ appId, limit: 1000 }));
          }
        } catch (error: any) {
          notify.error(error.message || 'Ошибка при удалении');
        }
      },
    });
  };

  const columns: ColumnsType<Segment> = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => (
        <Text strong style={{ fontSize: 14 }}>{name}</Text>
      ),
    },
    {
      title: 'Условий',
      key: 'conditions_count',
      width: 110,
      align: 'center',
      render: (_, record) => {
        const len = record.conditions?.length ?? 0;
        return (
          <Tag
            color={len === 0 ? 'default' : 'blue'}
            style={{ cursor: 'pointer' }}
          >
            {len === 0 ? 'Без условий' : `${len} усл.`}
          </Tag>
        );
      },
    },
    {
      title: 'Пользователей',
      dataIndex: 'user_count',
      key: 'user_count',
      width: 150,
      align: 'center',
      sorter: (a, b) => a.user_count - b.user_count,
      render: (count: number) => (
        <Badge
          count={formatters.number(count)}
          style={{ backgroundColor: getUserCountColor(count) }}
          overflowCount={9999999}
        />
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Редактировать">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                navigate(ROUTES.SEGMENT_EDIT.replace(':id', String(record.id)));
              }}
              disabled={!permissions.canManagePush}
            />
          </Tooltip>
          <Tooltip title="Удалить">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(record);
              }}
              disabled={!permissions.canManagePush}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (!appId) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Выберите приложение для просмотра сегментов"
      />
    );
  }

  const totalUsers = segments.reduce((sum, seg) => sum + seg.user_count, 0);

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Space>
            <Text strong style={{ fontSize: 16 }}>Сегменты пользователей</Text>
            <Badge
              count={`Всего: ${formatters.number(totalUsers)} польз.`}
              style={{ backgroundColor: '#1890ff' }}
            />
          </Space>
        </div>
        {permissions.canManagePush && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate(ROUTES.SEGMENT_CREATE)}
            size="large"
          >
            Создать сегмент
          </Button>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={segments}
        loading={loading}
        rowKey="id"
        onRow={(record) => ({
          onClick: () => {
            setExpandedRowKeys((prev) =>
              prev.includes(record.id)
                ? prev.filter((k) => k !== record.id)
                : [...prev, record.id]
            );
          },
          style: { cursor: 'pointer' },
        })}
        expandable={{
          expandedRowKeys,
          expandedRowRender: (record) => <ExpandedRow record={record} />,
          onExpand: (expanded, record) => {
            setExpandedRowKeys((prev) =>
              expanded
                ? [...prev, record.id]
                : prev.filter((k) => k !== record.id)
            );
          },
          rowExpandable: () => true,
        }}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `Всего сегментов: ${total}`,
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Space direction="vertical">
                  <Text>Сегменты не найдены</Text>
                  <Text type="secondary">
                    Создайте сегменты для таргетированной отправки push-уведомлений
                  </Text>
                </Space>
              }
            />
          ),
        }}
        scroll={{ x: 900 }}
      />

      {segments.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Space>
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Нажмите на строку, чтобы развернуть детали сегмента.
              Сегменты обновляются автоматически при изменении данных пользователей.
            </Text>
          </Space>
        </div>
      )}
    </div>
  );
};

export default SegmentList;